/**
 * Units and conversions — pure, dependency-free.
 *
 * SYNC RULE: `UNIT_KEYS` must be identical to `UNIT_KEYS` in
 * `packages/schema/src/common.ts`. Core stays dependency-free (it runs on the
 * server and on phones), so the vocabulary is duplicated here on purpose; the
 * schema package is the source of truth for the wire format and the two lists
 * are compared by a test in `packages/schema` (see that package).
 */
export const UNIT_KEYS = [
    'g',
    'kg',
    'oz',
    'lb',
    'ml',
    'l',
    'tsp',
    'tbsp',
    'fl_oz',
    'cup',
    'pint',
    'quart',
    'gallon',
    'count',
    'clove',
    'slice',
    'can',
    'package',
    'stick',
    'bunch',
    'pinch',
    'dash',
    'to_taste',
    'sprig',
    'head',
    'stalk',
    'jar',
    'bag',
    'box',
    'container',
    'link',
    'strip',
    'piece',
    'fillet',
    'ear',
    'leaf',
];
export class UnitConversionError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'UnitConversionError';
    }
}
const unit = (key, dimension, toBase, metric, singular, plural, aliases) => ({ key, dimension, toBase, metric, singular, plural, aliases });
export const UNITS = {
    // mass (base: gram)
    g: unit('g', 'mass', 1, true, 'g', 'g', ['g', 'gram', 'grams', 'gr']),
    kg: unit('kg', 'mass', 1000, true, 'kg', 'kg', ['kg', 'kilogram', 'kilograms', 'kilo', 'kilos']),
    oz: unit('oz', 'mass', 28.349523125, false, 'oz', 'oz', ['oz', 'ounce', 'ounces']),
    lb: unit('lb', 'mass', 453.59237, false, 'lb', 'lb', ['lb', 'lbs', 'pound', 'pounds', '#']),
    // volume (base: millilitre)
    ml: unit('ml', 'volume', 1, true, 'ml', 'ml', [
        'ml',
        'milliliter',
        'milliliters',
        'millilitre',
        'millilitres',
        'cc',
    ]),
    l: unit('l', 'volume', 1000, true, 'l', 'l', ['l', 'liter', 'liters', 'litre', 'litres']),
    tsp: unit('tsp', 'volume', 4.92892159375, false, 'tsp', 'tsp', [
        'tsp',
        'tsps',
        'teaspoon',
        'teaspoons',
    ]),
    tbsp: unit('tbsp', 'volume', 14.78676478125, false, 'tbsp', 'tbsp', [
        'tbsp',
        'tbsps',
        'tbs',
        'tbl',
        'tablespoon',
        'tablespoons',
    ]),
    fl_oz: unit('fl_oz', 'volume', 29.5735295625, false, 'fl oz', 'fl oz', [
        'fl oz',
        'floz',
        'fl_oz',
        'fluid ounce',
        'fluid ounces',
    ]),
    cup: unit('cup', 'volume', 236.5882365, false, 'cup', 'cups', ['cup', 'cups', 'c']),
    pint: unit('pint', 'volume', 473.176473, false, 'pint', 'pints', ['pint', 'pints', 'pt']),
    quart: unit('quart', 'volume', 946.352946, false, 'quart', 'quarts', ['quart', 'quarts', 'qt']),
    gallon: unit('gallon', 'volume', 3785.411784, false, 'gallon', 'gallons', [
        'gallon',
        'gallons',
        'gal',
    ]),
    // count-like (base: count; each kind is its own thing — see convert())
    count: unit('count', 'count', 1, false, '', '', ['count', 'each', 'ea', 'whole']),
    clove: unit('clove', 'count', 1, false, 'clove', 'cloves', ['clove', 'cloves']),
    slice: unit('slice', 'count', 1, false, 'slice', 'slices', ['slice', 'slices']),
    can: unit('can', 'count', 1, false, 'can', 'cans', ['can', 'cans']),
    package: unit('package', 'count', 1, false, 'package', 'packages', [
        'package',
        'packages',
        'pkg',
        'pkgs',
        'packet',
        'packets',
    ]),
    stick: unit('stick', 'count', 1, false, 'stick', 'sticks', ['stick', 'sticks']),
    bunch: unit('bunch', 'count', 1, false, 'bunch', 'bunches', ['bunch', 'bunches']),
    pinch: unit('pinch', 'count', 1, false, 'pinch', 'pinches', ['pinch', 'pinches']),
    dash: unit('dash', 'count', 1, false, 'dash', 'dashes', ['dash', 'dashes']),
    to_taste: unit('to_taste', 'count', 1, false, 'to taste', 'to taste', [
        'to taste',
        'to_taste',
        'as needed',
    ]),
    sprig: unit('sprig', 'count', 1, false, 'sprig', 'sprigs', ['sprig', 'sprigs']),
    head: unit('head', 'count', 1, false, 'head', 'heads', ['head', 'heads']),
    stalk: unit('stalk', 'count', 1, false, 'stalk', 'stalks', ['stalk', 'stalks']),
    jar: unit('jar', 'count', 1, false, 'jar', 'jars', ['jar', 'jars']),
    bag: unit('bag', 'count', 1, false, 'bag', 'bags', ['bag', 'bags']),
    box: unit('box', 'count', 1, false, 'box', 'boxes', ['box', 'boxes']),
    container: unit('container', 'count', 1, false, 'container', 'containers', [
        'container',
        'containers',
    ]),
    link: unit('link', 'count', 1, false, 'link', 'links', ['link', 'links']),
    strip: unit('strip', 'count', 1, false, 'strip', 'strips', ['strip', 'strips']),
    piece: unit('piece', 'count', 1, false, 'piece', 'pieces', ['piece', 'pieces', 'pc', 'pcs']),
    fillet: unit('fillet', 'count', 1, false, 'fillet', 'fillets', ['fillet', 'fillets']),
    ear: unit('ear', 'count', 1, false, 'ear', 'ears', ['ear', 'ears']),
    leaf: unit('leaf', 'count', 1, false, 'leaf', 'leaves', ['leaf', 'leaves']),
};
const BASE_UNIT = {
    mass: 'g',
    volume: 'ml',
    count: 'count',
};
const ALIAS_INDEX = (() => {
    const m = new Map();
    for (const key of UNIT_KEYS) {
        for (const alias of UNITS[key].aliases)
            m.set(alias, key);
    }
    return m;
})();
/** Metadata for a unit; throws `UNKNOWN_UNIT` for anything outside the vocabulary (untyped callers). */
export function unitInfo(key) {
    if (!isUnitKey(key))
        throw new UnitConversionError('UNKNOWN_UNIT', `Unknown unit "${key}"`);
    return UNITS[key];
}
export function isUnitKey(value) {
    return Object.prototype.hasOwnProperty.call(UNITS, value);
}
/**
 * Parse a unit word as written in a recipe. Case-insensitive except for the
 * single letters `T` (tablespoon) and `t` (teaspoon). Trailing periods and
 * plural `s`/`es` are tolerated. Returns null when unrecognised.
 */
export function parseUnit(text) {
    // Strip trailing periods with a loop rather than /\.+$/ — the regex form
    // backtracks quadratically on long runs of dots (CodeQL js/polynomial-redos).
    let trimmed = text.trim();
    let end = trimmed.length;
    while (end > 0 && trimmed.charCodeAt(end - 1) === 46 /* '.' */)
        end--;
    trimmed = trimmed.slice(0, end);
    if (trimmed === 'T')
        return 'tbsp';
    if (trimmed === 't')
        return 'tsp';
    const norm = trimmed.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
    if (norm === '')
        return null;
    const direct = ALIAS_INDEX.get(norm);
    if (direct)
        return direct;
    if (norm.endsWith('es')) {
        const hit = ALIAS_INDEX.get(norm.slice(0, -2));
        if (hit)
            return hit;
    }
    if (norm.endsWith('s')) {
        const hit = ALIAS_INDEX.get(norm.slice(0, -1));
        if (hit)
            return hit;
    }
    return null;
}
/**
 * Conversion options from an ingredient row's nullable columns
 * (`density_g_per_ml`, `avg_unit_weight_g`); null and undefined are left out.
 */
export function ingredientConvertOptions(densityGPerMl, gramsPerUnit) {
    const opts = {};
    if (densityGPerMl != null)
        opts.densityGPerMl = densityGPerMl;
    if (gramsPerUnit != null)
        opts.gramsPerUnit = gramsPerUnit;
    return opts;
}
function checkedDensity(opts, from, to) {
    const density = opts.densityGPerMl;
    if (density === undefined) {
        throw new UnitConversionError('MISSING_DENSITY', `Converting ${from} to ${to} needs a density (g/ml)`);
    }
    if (!Number.isFinite(density) || density <= 0) {
        throw new UnitConversionError('INVALID_DENSITY', `Density must be > 0 g/ml, got ${density}`);
    }
    return density;
}
function checkedUnitWeight(opts, from, to) {
    const grams = opts.gramsPerUnit;
    if (grams === undefined) {
        throw new UnitConversionError('MISSING_UNIT_WEIGHT', `Converting ${from} to ${to} needs an average weight per item (g)`);
    }
    if (!Number.isFinite(grams) || grams <= 0) {
        throw new UnitConversionError('INVALID_UNIT_WEIGHT', `Average weight per item must be > 0 g, got ${grams}`);
    }
    return grams;
}
/** A non-count quantity in grams (volume goes through the density). */
function toGrams(qty, info, opts, from, to) {
    return info.dimension === 'mass'
        ? qty * info.toBase
        : qty * info.toBase * checkedDensity(opts, from, to);
}
/** Grams expressed in a non-count unit (volume goes through the density). */
function fromGrams(grams, info, opts, from, to) {
    return info.dimension === 'mass'
        ? grams / info.toBase
        : grams / checkedDensity(opts, from, to) / info.toBase;
}
/**
 * Convert a quantity between units.
 * - Same dimension (mass or volume): exact factor conversion.
 * - Mass <-> volume: only with `densityGPerMl`; otherwise `MISSING_DENSITY`.
 * - Count-like units are distinct things: `clove` never becomes `slice`
 *   (`INCOMPATIBLE_UNITS`). The generic `count` unit is the base, so any
 *   count-like unit converts to/from `count` as an identity.
 * - The generic `count` unit converts to mass with `gramsPerUnit` (otherwise
 *   `MISSING_UNIT_WEIGHT`), and to volume with both `gramsPerUnit` and
 *   `densityGPerMl`. Other count-like units never leave their dimension.
 */
export function convert(qty, from, to, opts = {}) {
    if (!Number.isFinite(qty)) {
        throw new UnitConversionError('INVALID_QUANTITY', `Quantity must be a finite number, got ${qty}`);
    }
    if (from === to)
        return qty;
    const a = unitInfo(from);
    const b = unitInfo(to);
    if (a.dimension === 'count' && b.dimension === 'count') {
        if (from === 'count' || to === 'count')
            return qty;
        throw new UnitConversionError('INCOMPATIBLE_UNITS', `Cannot convert ${from} to ${to}`);
    }
    if (a.dimension === 'count' || b.dimension === 'count') {
        // Exactly one side is count-like; only the generic `count` has a weight.
        const countSide = a.dimension === 'count' ? from : to;
        if (countSide !== 'count') {
            throw new UnitConversionError('INCOMPATIBLE_UNITS', `Cannot convert ${from} to ${to}`);
        }
        const perItem = checkedUnitWeight(opts, from, to);
        if (a.dimension === 'count')
            return fromGrams(qty * perItem, b, opts, from, to);
        return toGrams(qty, a, opts, from, to) / perItem;
    }
    if (a.dimension === b.dimension)
        return (qty * a.toBase) / b.toBase;
    return fromGrams(toGrams(qty, a, opts, from, to), b, opts, from, to);
}
/** Express a quantity in its dimension's base unit (g / ml / count). With a density, mass and volume both go to grams. */
export function toCanonical(qty, unit, densityGPerMl) {
    const info = unitInfo(unit);
    const target = info.dimension === 'volume' && densityGPerMl !== undefined ? 'g' : BASE_UNIT[info.dimension];
    const opts = densityGPerMl === undefined ? {} : { densityGPerMl };
    return { qty: convert(qty, unit, target, opts), unit: target };
}
/** Inverse of `toCanonical`: express a canonical quantity in `unit`. */
export function fromCanonical(canonical, unit, densityGPerMl) {
    const opts = densityGPerMl === undefined ? {} : { densityGPerMl };
    return convert(canonical.qty, canonical.unit, unit, opts);
}
/**
 * Sum two quantities for list aggregation. The result is expressed in `a`'s
 * unit. Returns null when the two cannot be combined (different dimensions
 * without a density, or distinct count-like units) so callers can keep them
 * as separate lines instead of guessing.
 */
export function addQuantities(a, b, opts = {}) {
    try {
        return { qty: a.qty + convert(b.qty, b.unit, a.unit, opts), unit: a.unit };
    }
    catch {
        // convert() only throws UnitConversionError: anything it rejects is "not combinable".
        return null;
    }
}
// ---------------------------------------------------------------- numbers --
const UNICODE_FRACTIONS = {
    '⅛': 1 / 8,
    '¼': 1 / 4,
    '⅓': 1 / 3,
    '⅜': 3 / 8,
    '½': 1 / 2,
    '⅝': 5 / 8,
    '⅔': 2 / 3,
    '¾': 3 / 4,
    '⅞': 7 / 8,
};
/** Fractions we render, in ascending order. */
const DISPLAY_FRACTIONS = [
    [1 / 8, '⅛'],
    [1 / 4, '¼'],
    [1 / 3, '⅓'],
    [3 / 8, '⅜'],
    [1 / 2, '½'],
    [5 / 8, '⅝'],
    [2 / 3, '⅔'],
    [3 / 4, '¾'],
    [7 / 8, '⅞'],
];
const FRACTION_TOLERANCE = 0.02;
const UF = '[⅛¼⅓⅜½⅝⅔¾⅞]';
// mixed "1 1/2" | "1½" | "1/2" | "½" | "1.5" | ".5" | "1"
const NUM = `(?:\\d+\\s+\\d+/\\d+|\\d+\\s*${UF}|\\d+/\\d+|${UF}|\\d+\\.\\d+|\\.\\d+|\\d+)`;
const QUALIFIER = '(?:just over|just under|about|approximately|approx\\.?|roughly|around|scant|heaping|heaped|generous|nearly|almost|a good)';
const QUANTITY_RE = new RegExp(`^(?<pre>(?:${QUALIFIER}\\s+)*)(?<a>${NUM})(?:\\s*(?:-|–|—|to)\\s*(?<b>${NUM}))?(?<rest>.*)$`, 'is');
function numberFromToken(token) {
    const t = token.trim();
    const uf = UNICODE_FRACTIONS[t.slice(-1)];
    if (uf !== undefined) {
        const whole = t.slice(0, -1).trim();
        return whole === '' ? uf : Number(whole) + uf;
    }
    const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t);
    if (mixed)
        return fraction(Number(mixed[1]), Number(mixed[2]), Number(mixed[3]));
    const simple = /^(\d+)\/(\d+)$/.exec(t);
    if (simple)
        return fraction(0, Number(simple[1]), Number(simple[2]));
    return Number(t);
}
function fraction(whole, num, den) {
    if (den === 0)
        return null;
    return whole + num / den;
}
/**
 * Parse the leading number(s) of an ingredient line: integers, decimals,
 * unicode and ASCII fractions, mixed numbers, and ranges. Qualifier words
 * before the number ("just over 1/2 lb") are kept in `rest` as a note.
 */
export function parseQuantity(text) {
    const m = QUANTITY_RE.exec(text.trim());
    if (!m?.groups)
        return { qty: null, rest: text.trim() };
    const { pre, a, b, rest } = m.groups;
    const qty = numberFromToken(a);
    if (qty === null)
        return { qty: null, rest: text.trim() };
    const remainder = [pre.trim(), rest.trim()].filter((s) => s !== '').join(' ');
    if (b === undefined)
        return { qty, rest: remainder };
    const qtyMax = numberFromToken(b);
    return qtyMax === null ? { qty, rest: remainder } : { qty, qtyMax, rest: remainder };
}
function formatFraction(q) {
    const whole = Math.floor(q + 1e-9);
    const frac = q - whole;
    if (frac < FRACTION_TOLERANCE)
        return String(whole);
    if (frac > 1 - FRACTION_TOLERANCE)
        return String(whole + 1);
    let best;
    for (const candidate of DISPLAY_FRACTIONS) {
        if (Math.abs(candidate[0] - frac) <= FRACTION_TOLERANCE) {
            best = candidate;
            break;
        }
    }
    if (!best)
        return formatDecimal(q);
    return whole === 0 ? best[1] : `${whole} ${best[1]}`;
}
function formatDecimal(q) {
    const places = q >= 100 ? 0 : q >= 10 ? 1 : 2;
    return String(Number(q.toFixed(places)));
}
/** Friendly rendering: "1 ½ cups", "2 tbsp", "250 g", "½ clove", "3" (count), "to taste". */
export function formatQuantity(qty, unit, opts = {}) {
    let info = unitInfo(unit);
    if (unit === 'to_taste')
        return info.singular;
    if (!Number.isFinite(qty) || qty < 0) {
        throw new UnitConversionError('INVALID_QUANTITY', `Cannot format quantity ${qty}`);
    }
    let q = qty;
    if ((opts.system ?? 'us') === 'metric' && info.dimension !== 'count' && !info.metric) {
        const base = BASE_UNIT[info.dimension];
        q = convert(qty, unit, base);
        info = UNITS[base];
        if (q >= 1000) {
            q /= 1000;
            info = UNITS[info.dimension === 'mass' ? 'kg' : 'l'];
        }
    }
    const number = info.metric ? formatDecimal(q) : formatFraction(q);
    const name = q > 1 ? info.plural : info.singular;
    return name === '' ? number : `${number} ${name}`;
}
//# sourceMappingURL=units.js.map